import { cn } from '@/lib/tailwind.utils'
import React from 'react'
import { accentLevelBgMap } from '../types'
import type { AccentColor, DataRequirementRow, DatasetCategory } from '../types'

const LEVELS = [1, 2, 3, 4] as const

interface DataRequirementsTableProps {
  rows: DataRequirementRow[]
  accentColor: AccentColor
}

const DataRequirementsTable = ({ rows, accentColor }: DataRequirementsTableProps) => {
  const categories = Array.from(new Set(rows.map((r) => r.category))) as DatasetCategory[]

  const rowsByCategory = categories.reduce<Record<DatasetCategory, DataRequirementRow[]>>(
    (acc, cat) => {
      acc[cat] = rows.filter((r) => r.category === cat)
      return acc
    },
    {} as Record<DatasetCategory, DataRequirementRow[]>,
  )

  return (
    <div className="overflow-hidden rounded-lg">
      <table className="w-full">
        <thead>
          <tr>
            <th className="w-1/3 py-4 pl-4 text-left text-xs font-medium text-foreground">
              Dataset
            </th>
            {LEVELS.map((l) => (
              <th key={l} className="py-3 text-left text-xs font-semibold text-foreground">
                LEVEL {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((category) => (
            <React.Fragment key={category}>
              <tr className="bg-neutral">
                <td colSpan={5} className="py-4 pl-4 text-sm font-semibold text-foreground">
                  {category}
                </td>
              </tr>
              {rowsByCategory[category].map((row, rowIndex) => (
                <tr key={row.dataset}>
                  <td className="py-3 pl-4 text-sm text-foreground">{row.dataset}</td>
                  {LEVELS.map((l) => (
                    <td key={l} className="py-3 pr-0">
                      {l >= row.startLevel && (
                        <div className="overflow-hidden">
                          <div
                            className={cn(
                              'h-4 origin-left animate-in zoom-in-0 rounded-full duration-500 fill-mode-both',
                              accentLevelBgMap[accentColor][l - 1],
                              l === row.startLevel && 'rounded-l-full rounded-r-none',
                              l > row.startLevel && l < 4 && 'rounded-none',
                              l === 4 && l !== row.startLevel && 'rounded-l-none rounded-r-full',
                              l === 4 && l === row.startLevel && 'rounded-full',
                            )}
                            style={{
                              animationDelay: `${rowIndex * 70 + (l - row.startLevel) * 90}ms`,
                            }}
                          />
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default DataRequirementsTable
